/**
 * Control Portal - Customer Detail Page
 * View and manage a specific customer tenant
 */
import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { controlApi } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  ArrowLeft, Building2, Users, MapPin, Wifi, Settings,
  Save, Loader2, RefreshCw, Plus, ChevronRight, Activity
} from 'lucide-react';
import TelemetryConfigEditor from '@/components/control/TelemetryConfigEditor';

export default function TenantDetailPage() {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tenant, setTenant] = useState(null);
  const [stats, setStats] = useState({});
  const [locations, setLocations] = useState([]);
  const [users, setUsers] = useState([]);
  const [devices, setDevices] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({});

  const fetchTenantData = useCallback(async () => {
    try {
      setLoading(true);
      const [tenantRes, statsRes, locationsRes, usersRes, devicesRes] = await Promise.all([
        controlApi.getTenant(tenantId),
        controlApi.getTenantStats(tenantId),
        controlApi.getLocations(tenantId, { limit: 100 }),
        controlApi.getUsers(tenantId, { limit: 100 }),
        controlApi.getDevices(tenantId, { limit: 100 })
      ]);
      
      setTenant(tenantRes.data);
      setStats(statsRes.data);
      setLocations(locationsRes.data);
      setUsers(usersRes.data);
      setDevices(devicesRes.data);
      setFormData(tenantRes.data);
    } catch (error) {
      toast.error('Failed to load tenant data');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    fetchTenantData();
  }, [fetchTenantData]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await controlApi.updateTenant(tenantId, formData);
      toast.success('Tenant updated successfully');
      setEditMode(false);
      fetchTenantData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update tenant');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (!tenant) {
    return (
      <div className="p-6 text-center">
        <p className="text-slate-400">Tenant not found</p>
        <Button onClick={() => navigate('/control/tenants')} className="mt-4">
          Back to Tenants
        </Button>
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="tenant-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/control/tenants')}
            className="text-slate-400"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-3">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: tenant.primary_color || '#3b82f6' }}
            >
              <Building2 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">{tenant.name}</h1>
              <p className="text-slate-400 font-mono">{tenant.code}</p>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={fetchTenantData}
            className="border-slate-700 text-slate-300"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          {editMode ? (
            <>
              <Button variant="ghost" onClick={() => setEditMode(false)}>Cancel</Button>
              <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Save Changes
              </Button>
            </>
          ) : (
            <Button onClick={() => setEditMode(true)} className="bg-blue-600 hover:bg-blue-700">
              <Settings className="w-4 h-4 mr-2" />
              Edit
            </Button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Locations', value: stats.locations_count || 0, icon: MapPin, color: 'blue' },
          { label: 'Users', value: stats.users_count || 0, icon: Users, color: 'green' },
          { label: 'Devices', value: stats.devices_count || 0, icon: Wifi, color: 'teal' },
          { label: 'Work Orders', value: stats.work_orders_count || 0, icon: Building2, color: 'amber' },
        ].map((stat, i) => {
          const Icon = stat.icon;
          return (
            <div key={`idx-${i}`} className="bg-slate-900 rounded-xl p-4 border border-slate-800">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center bg-${stat.color}-500/20`}>
                  <Icon className={`w-5 h-5 text-${stat.color}-400`} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stat.value}</p>
                  <p className="text-slate-400 text-sm">{stat.label}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Tabs */}
      <Tabs defaultValue="details" className="space-y-4">
        <TabsList className="bg-slate-900 border border-slate-800">
          <TabsTrigger value="details" className="data-[state=active]:bg-blue-600">Details</TabsTrigger>
          <TabsTrigger value="locations" className="data-[state=active]:bg-blue-600">Locations</TabsTrigger>
          <TabsTrigger value="users" className="data-[state=active]:bg-blue-600">Users</TabsTrigger>
          <TabsTrigger value="devices" className="data-[state=active]:bg-blue-600">Devices</TabsTrigger>
          <TabsTrigger value="telemetry" className="data-[state=active]:bg-blue-600" data-testid="tab-telemetry">
            <Activity className="w-3.5 h-3.5 mr-1" /> Telemetry
          </TabsTrigger>
        </TabsList>

        {/* Details Tab */}
        <TabsContent value="details" className="space-y-4">
          <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Company Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-400">Company Name</Label>
                {editMode ? (
                  <Input
                    value={formData.name || ''}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="bg-slate-800 border-slate-700 text-white mt-1"
                  />
                ) : (
                  <p className="text-white mt-1">{tenant.name}</p>
                )}
              </div>
              <div>
                <Label className="text-slate-400">Tenant Code</Label>
                <p className="text-white font-mono mt-1">{tenant.code}</p>
              </div>
              <div>
                <Label className="text-slate-400">Status</Label>
                <div className="mt-1">
                  <Badge variant={tenant.status === 'active' ? 'default' : 'secondary'}>
                    {tenant.status}
                  </Badge>
                </div>
              </div>
              <div>
                <Label className="text-slate-400">Primary Color</Label>
                {editMode ? (
                  <div className="flex gap-2 mt-1">
                    <Input
                      type="color"
                      value={formData.primary_color || '#3b82f6'}
                      onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                      className="w-12 h-9 p-1 bg-slate-800 border-slate-700"
                    />
                    <Input
                      value={formData.primary_color || ''}
                      onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                      className="bg-slate-800 border-slate-700 text-white"
                      placeholder="#3b82f6"
                    />
                  </div>
                ) : (
                  <div className="flex items-center gap-2 mt-1">
                    <div
                      className="w-6 h-6 rounded"
                      style={{ backgroundColor: tenant.primary_color || '#3b82f6' }}
                    />
                    <span className="text-white font-mono">{tenant.primary_color || '#3b82f6'}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Contact Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-400">Contact Name</Label>
                {editMode ? (
                  <Input
                    value={formData.contact_name || ''}
                    onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                    className="bg-slate-800 border-slate-700 text-white mt-1"
                  />
                ) : (
                  <p className="text-white mt-1">{tenant.contact_name || '-'}</p>
                )}
              </div>
              <div>
                <Label className="text-slate-400">Contact Email</Label>
                {editMode ? (
                  <Input
                    type="email"
                    value={formData.contact_email || ''}
                    onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                    className="bg-slate-800 border-slate-700 text-white mt-1"
                  />
                ) : (
                  <p className="text-white mt-1">{tenant.contact_email || '-'}</p>
                )}
              </div>
              <div>
                <Label className="text-slate-400">Phone</Label>
                {editMode ? (
                  <Input
                    value={formData.company_phone || ''}
                    onChange={(e) => setFormData({ ...formData, company_phone: e.target.value })}
                    className="bg-slate-800 border-slate-700 text-white mt-1"
                  />
                ) : (
                  <p className="text-white mt-1">{tenant.company_phone || '-'}</p>
                )}
              </div>
              <div className="md:col-span-2">
                <Label className="text-slate-400">Address</Label>
                {editMode ? (
                  <Input
                    value={formData.company_address || ''}
                    onChange={(e) => setFormData({ ...formData, company_address: e.target.value })}
                    className="bg-slate-800 border-slate-700 text-white mt-1"
                  />
                ) : (
                  <p className="text-white mt-1">{tenant.company_address || '-'}</p>
                )}
              </div>
            </div>
          </div>

          <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Compliance Documents</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-400">Drug License 20</Label>
                {editMode ? (
                  <Input
                    value={formData.drug_license_20 || ''}
                    onChange={(e) => setFormData({ ...formData, drug_license_20: e.target.value })}
                    className="bg-slate-800 border-slate-700 text-white mt-1"
                  />
                ) : (
                  <p className="text-white mt-1">{tenant.drug_license_20 || '-'}</p>
                )}
              </div>
              <div>
                <Label className="text-slate-400">Drug License 21</Label>
                {editMode ? (
                  <Input
                    value={formData.drug_license_21 || ''}
                    onChange={(e) => setFormData({ ...formData, drug_license_21: e.target.value })}
                    className="bg-slate-800 border-slate-700 text-white mt-1"
                  />
                ) : (
                  <p className="text-white mt-1">{tenant.drug_license_21 || '-'}</p>
                )}
              </div>
              <div>
                <Label className="text-slate-400">GST Number</Label>
                {editMode ? (
                  <Input
                    value={formData.gst_number || ''}
                    onChange={(e) => setFormData({ ...formData, gst_number: e.target.value })}
                    className="bg-slate-800 border-slate-700 text-white mt-1"
                  />
                ) : (
                  <p className="text-white mt-1">{tenant.gst_number || '-'}</p>
                )}
              </div>
              <div>
                <Label className="text-slate-400">PAN Number</Label>
                {editMode ? (
                  <Input
                    value={formData.pan_number || ''}
                    onChange={(e) => setFormData({ ...formData, pan_number: e.target.value })}
                    className="bg-slate-800 border-slate-700 text-white mt-1"
                  />
                ) : (
                  <p className="text-white mt-1">{tenant.pan_number || '-'}</p>
                )}
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Locations Tab */}
        <TabsContent value="locations">
          <div className="bg-slate-900 rounded-xl border border-slate-800">
            <div className="p-4 border-b border-slate-800 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white">Organization Structure</h3>
              <Button
                size="sm"
                onClick={() => navigate(`/control/tenants/${tenantId}/locations`)}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Manage Locations
              </Button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-800">
                    <th className="text-left p-3 text-slate-400 font-medium">Name</th>
                    <th className="text-left p-3 text-slate-400 font-medium">Type</th>
                    <th className="text-left p-3 text-slate-400 font-medium">City</th>
                    <th className="text-center p-3 text-slate-400 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {locations.slice(0, 10).map((loc) => (
                    <tr key={loc.id} className="border-b border-slate-800/50 hover:bg-slate-800/50">
                      <td className="p-3 text-white">{loc.name}</td>
                      <td className="p-3">
                        <Badge variant="outline" className="border-slate-600 capitalize">{loc.type}</Badge>
                      </td>
                      <td className="p-3 text-slate-300">{loc.city || '-'}</td>
                      <td className="p-3 text-center">
                        <Badge variant={loc.status === 'active' ? 'default' : 'secondary'}>{loc.status}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {locations.length === 0 && (
                <div className="text-center py-8 text-slate-500">
                  <MapPin className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No locations defined</p>
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users">
          <div className="bg-slate-900 rounded-xl border border-slate-800">
            <div className="p-4 border-b border-slate-800 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white">User Management</h3>
              <Button
                size="sm"
                onClick={() => navigate(`/control/tenants/${tenantId}/users`)}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Manage Users
              </Button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-800">
                    <th className="text-left p-3 text-slate-400 font-medium">Name</th>
                    <th className="text-left p-3 text-slate-400 font-medium">Email</th>
                    <th className="text-left p-3 text-slate-400 font-medium">Phone</th>
                    <th className="text-center p-3 text-slate-400 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {users.slice(0, 10).map((user) => (
                    <tr key={user.id} className="border-b border-slate-800/50 hover:bg-slate-800/50">
                      <td className="p-3 text-white">{user.full_name}</td>
                      <td className="p-3 text-slate-300">{user.email}</td>
                      <td className="p-3 text-slate-300">{user.phone || '-'}</td>
                      <td className="p-3 text-center">
                        <Badge variant={user.status === 'active' ? 'default' : 'secondary'}>{user.status}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {users.length === 0 && (
                <div className="text-center py-8 text-slate-500">
                  <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No users found</p>
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Devices Tab */}
        <TabsContent value="devices">
          <div className="bg-slate-900 rounded-xl border border-slate-800">
            <div className="p-4 border-b border-slate-800 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white">Device Inventory</h3>
              <Button
                size="sm"
                onClick={() => navigate(`/control/tenants/${tenantId}/devices`)}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Manage Devices
              </Button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-800">
                    <th className="text-left p-3 text-slate-400 font-medium">Device UID</th>
                    <th className="text-left p-3 text-slate-400 font-medium">IMEI</th>
                    <th className="text-left p-3 text-slate-400 font-medium">Last Seen</th>
                    <th className="text-center p-3 text-slate-400 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {devices.slice(0, 10).map((device) => (
                    <tr key={device.id} className="border-b border-slate-800/50 hover:bg-slate-800/50">
                      <td className="p-3 text-white font-mono">{device.device_uid}</td>
                      <td className="p-3 text-slate-300">{device.imei_number || '-'}</td>
                      <td className="p-3 text-slate-300">
                        {device.last_seen_at ? new Date(device.last_seen_at).toLocaleString() : 'Never'}
                      </td>
                      <td className="p-3 text-center">
                        <Badge variant={device.status === 'active' ? 'default' : 'secondary'}>{device.status}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {devices.length === 0 && (
                <div className="text-center py-8 text-slate-500">
                  <Wifi className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No devices assigned</p>
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Telemetry Tab */}
        <TabsContent value="telemetry" className="space-y-4">
          <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Activity className="w-5 h-5 text-blue-400" />
              Telemetry Channels
            </h3>
            <TelemetryConfigEditor tenantId={tenantId} />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
